/**
 * DemoRoleContext
 * Provides demo role toggling (Broker / Underwriter) throughout the app.
 * Does NOT change JWT auth — only controls UI read-only mode for demo purposes.
 *
 * isDemo: true when inside a DemoRoleProvider (gates demo-only features like
 * the MobileInspectorSimulator).
 */

import { createContext, useContext, useState, type ReactNode } from 'react';
import type { DemoRole } from '../components/RoleSelector';

interface DemoRoleContextValue {
  demoRole: DemoRole;
  setDemoRole: (role: DemoRole) => void;
  isReadOnly: boolean;
  isDemo: boolean;
}

const DemoRoleContext = createContext<DemoRoleContextValue>({
  demoRole: 'broker',
  setDemoRole: () => {},
  isReadOnly: false,
  isDemo: false,
});

export function DemoRoleProvider({ children }: { children: ReactNode }) {
  const [demoRole, setDemoRole] = useState<DemoRole>('broker');

  return (
    <DemoRoleContext.Provider
      value={{
        demoRole,
        setDemoRole,
        isReadOnly: demoRole === 'underwriter',
        isDemo: true,
      }}
    >
      {children}
    </DemoRoleContext.Provider>
  );
}

export function useDemoRole() {
  return useContext(DemoRoleContext);
}
